import React from "react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import "./index.css";
import { get } from "services/http";
import { ConfigurationDetails } from "features/types";
import Form from "./Form";

export interface StripeCard {
  address_city: string | null;
  address_country: string | null;
  address_line1: string | null;
  address_line1_check: string | null;
  address_line2: string | null;
  address_state: string | null;
  address_zip: string;
  address_zip_check: string;
  brand: string;
  country: string;
  cvc_check: string;
  dynamic_last4: string | null;
  exp_month: number;
  exp_year: number;
  funding: string;
  id: string;
  last4: string;
  name: string | null;
  object: string;
  tokenization_method: string | null;
}
export interface StripeToken {
  card: StripeCard;
  client_ip: string;
  created: string;
  id: string;
  livemode: boolean;
  object: string;
  type: string;
  used: boolean;
}

export interface StripeTokenResponse {
  nameOnCard: string;
  postalCode: string;
  token: StripeToken;
}

export interface StripeTokenError {
  error: {
    code: string;
    message: string;
    type: string;
  };
}

// What actually happened with the token, so the wallet sheet can report it:
// "completed" and "deferred" finish the sheet as success ("deferred" means the
// payment method was saved and the purchase continues on-page, e.g. behind
// phone verification), "failed" shows the sheet's failure state.  A thrown
// error also fails the sheet.  A plain void return maps to success, which
// keeps simple save-card consumers working, but purchase flows should return
// an explicit outcome from every path.
export type TokenReceivedOutcome = "completed" | "failed" | "deferred";

export interface StripePaymentFormProps {
  btnText?: string;
  // May return a promise: the wallet sheet stays open until it settles and
  // reports the returned outcome (see TokenReceivedOutcome).
  onTokenReceived: (
    error: StripeTokenError | undefined,
    response?: StripeTokenResponse
  ) => void | TokenReceivedOutcome | Promise<void | TokenReceivedOutcome>;
  disabled?: boolean;
  loading?: boolean;
  showAgreementText?: boolean;
  beforeButton?: React.ReactNode;
  // Purchase amount in dollars shown on the Apple Pay / Google Pay sheet.
  // - undefined: save-card context; the sheet runs a $0 card verification.
  // - null (or NaN): purchase amount still loading; the wallet button stays
  //   blocked so the sheet can never open without the final amount.
  // - 0: a real zero due today; the sheet shows $0.00 and stays usable.
  // - > 0: the final amount due today.
  walletAmount?: number | null;
}

// https://github.com/stripe/stripe-js/issues/43#issuecomment-643840075
let stripePromise: any;
const getStripe = () => {
  if (!stripePromise) {
    stripePromise = get({
      url: `/configuration`,
    })
      .then((data) => {
        const { billingProviderPublicKey } = data as ConfigurationDetails;
        return billingProviderPublicKey;
      })
      .then((billingProviderPublicKey) => loadStripe(billingProviderPublicKey));
  }
  return stripePromise;
};

const StripePaymentForm: React.FC<StripePaymentFormProps> = (props) => {
  return (
    <Elements stripe={getStripe()}>
      <Form {...props} />
    </Elements>
  );
};

export default StripePaymentForm;
